import { trpc } from '@lib/trpc';

export interface ChevronStatusData {
  key: string;
  number: number;
  label: string;
  color: string;
  description: string;
  count?: number;
}

export const useStatuses = () => {
  // Get all statuses with high priority and aggressive caching
  const { 
    data: statuses = [], 
    isLoading, 
    error,
    refetch 
  } = trpc.status.getAll.useQuery(undefined, {
    staleTime: 15 * 60 * 1000, // 15 minutes - more aggressive
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    retry: 3, // Retry failed requests
  });

  // Get chevron display data with high priority
  const { 
    data: chevronData = [],
    isLoading: isChevronLoading 
  } = trpc.status.getChevronData.useQuery(undefined, {
    staleTime: 15 * 60 * 1000, 
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });

  // Get invoice counts by status
  const { 
    data: invoiceCounts = {},
    isLoading: isCountsLoading 
  } = trpc.status.getInvoiceCounts.useQuery(undefined, {
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent for counts)
    gcTime: 5 * 60 * 1000,
  });

  // Helper functions with error handling
  const getStatusByKey = (key: string) => {
    if (!statuses || statuses.length === 0) return null;
    return statuses.find(status => status.key === key) || null;
  };

  const getStatusByOrder = (orderNum: number) => {
    if (!statuses || statuses.length === 0) return null;
    return statuses.find(status => status.orderNum === orderNum) || null;
  };

  const getNextStatus = (currentKey: string) => {
    if (!currentKey || !statuses || statuses.length === 0) return null;
    const current = getStatusByKey(currentKey);
    if (!current) return null;
    return getStatusByOrder(current.orderNum + 1);
  };

  const getPreviousStatus = (currentKey: string) => {
    if (!currentKey || !statuses || statuses.length === 0) return null;
    const current = getStatusByKey(currentKey);
    if (!current) return null;
    return getStatusByOrder(current.orderNum - 1);
  };

  // Get status options for filters/dropdowns
  const getStatusOptions = () => 
    statuses.map(status => ({
      value: status.key,
      label: status.label,
      color: status.color,
      count: invoiceCounts[status.key] || 0,
    }));

  // Get chevron data with counts
  const getChevronDataWithCounts = (): ChevronStatusData[] => 
    chevronData.map(status => ({
      ...status,
      count: invoiceCounts[status.key] || 0,
    }));

  return {
    // Raw data
    statuses,
    chevronData,
    invoiceCounts,
    
    // Loading states
    isLoading: isLoading || isChevronLoading,
    isCountsLoading,
    error,
    
    // Helper functions
    getStatusByKey,
    getStatusByOrder,
    getNextStatus,
    getPreviousStatus,
    getStatusOptions,
    getChevronDataWithCounts,
    
    // Actions
    refetch,
  };
};

// Hook for admin status management
export const useStatusAdmin = () => {
  const utils = trpc.useContext();
  
  const createMutation = trpc.status.create.useMutation({
    onSuccess: () => {
      // Invalidate all status queries
      utils.status.invalidate();
    },
  });

  const updateMutation = trpc.status.update.useMutation({
    onSuccess: () => {
      utils.status.invalidate();
    },
  });

  const reorderMutation = trpc.status.reorder.useMutation({
    onSuccess: () => {
      utils.status.invalidate();
    },
  });

  const deleteMutation = trpc.status.delete.useMutation({
    onSuccess: () => {
      utils.status.invalidate();
    },
  });

  return {
    createStatus: createMutation.mutateAsync,
    updateStatus: updateMutation.mutateAsync,
    reorderStatuses: reorderMutation.mutateAsync,
    deleteStatus: deleteMutation.mutateAsync,
    
    // Loading states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isReordering: reorderMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    reorderError: reorderMutation.error,
    deleteError: deleteMutation.error,
  };
};

// Hook for checking status transitions
export const useStatusTransitions = (currentStatus?: string) => {
  const { data: nextStatus } = trpc.status.getNextStatus.useQuery(
    { currentKey: currentStatus! },
    { enabled: !!currentStatus }
  );

  const canTransitionTo = trpc.status.canTransitionTo.useQuery;

  return {
    nextStatus,
    canTransitionTo,
  };
};
